import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCollection } from '@/hooks/useCollection';
import {
  createRow,
  updateRow,
  deleteRow,
  fetchSiteSettings,
  updateSiteSettings,
} from '@/lib/api';
import type { Page } from '@/lib/types';

interface BuiltinPage {
  path: string;
  label: string;
}

const BUILTIN_PAGES: BuiltinPage[] = [
  { path: '/about', label: 'About' },
  { path: '/services', label: 'Services' },
  { path: '/sermons', label: 'Sermons' },
  { path: '/events', label: 'Events' },
  { path: '/give', label: 'Give' },
  { path: '/contact', label: 'Contact' },
  { path: '/highlights', label: 'Highlights (Gallery)' },
  { path: '/testimonials', label: 'Testimonials' },
  { path: '/news', label: 'News' },
  { path: '/live', label: 'Watch Live' },
  { path: '/pureheart', label: 'PureHeart Program' },
];

interface CustomForm {
  title: string;
  slug: string;
  content: string;
  show_in_nav: boolean;
  is_published: boolean;
  sort_order: number;
}

const EMPTY_FORM: CustomForm = {
  title: '',
  slug: '',
  content: '',
  show_in_nav: true,
  is_published: true,
  sort_order: 0,
};

function slugify(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function PagesAdminPage() {
  const { rows: pages, loading, error, reload } = useCollection<Page>('pages', 'sort_order', true);

  const [hidden, setHidden] = useState<string[]>([]);
  const [hiddenLoading, setHiddenLoading] = useState(true);
  const [savingHidden, setSavingHidden] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CustomForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchSiteSettings()
      .then((s) => setHidden(s.hidden_nav_pages ?? []))
      .catch(() => setHidden([]))
      .finally(() => setHiddenLoading(false));
  }, []);

  const toggleBuiltin = async (path: string) => {
    const isHidden = hidden.includes(path);
    const next = isHidden ? hidden.filter((p) => p !== path) : [...hidden, path];
    setHidden(next);
    setSavingHidden(path);
    setToggleError(null);
    try {
      await updateSiteSettings({ hidden_nav_pages: next });
    } catch (err) {
      setHidden(hidden);
      setToggleError(err instanceof Error ? err.message : 'Failed to update visibility');
    } finally {
      setSavingHidden(null);
    }
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (row: Page) => {
    setEditingId(row.id);
    setForm({
      title: row.title,
      slug: row.slug,
      content: row.content ?? '',
      show_in_nav: row.show_in_nav,
      is_published: row.is_published,
      sort_order: row.sort_order ?? 0,
    });
    setFormError(null);
    setModalOpen(true);
  };

  const handleTitleChange = (title: string) => {
    setForm((p) => {
      const slug = editingId ? p.slug : slugify(title);
      return { ...p, title, slug };
    });
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setFormError('Please enter a page title.');
      return;
    }
    if (!form.slug.trim()) {
      setFormError('Please enter a URL slug.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload: Record<string, unknown> = {
        title: form.title.trim(),
        slug: slugify(form.slug),
        content: form.content,
        show_in_nav: form.show_in_nav,
        is_published: form.is_published,
        sort_order: form.sort_order,
      };
      if (editingId) {
        await updateRow('pages', editingId, payload);
      } else {
        await createRow('pages', payload);
      }
      setModalOpen(false);
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const toggleCustom = async (row: Page, field: 'show_in_nav' | 'is_published') => {
    setBusy(true);
    setToggleError(null);
    try {
      await updateRow('pages', row.id, { [field]: !row[field] });
      reload();
    } catch (err) {
      setToggleError(err instanceof Error ? err.message : 'Failed to update page');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      await deleteRow('pages', confirmDelete);
      setConfirmDelete(null);
      reload();
    } catch (err) {
      setToggleError(err instanceof Error ? err.message : 'Failed to delete page');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-heading font-semibold text-xl text-foreground-900">Pages</h2>
        <p className="text-sm text-foreground-500 mt-1">
          Show or hide pages in the website menu, and create custom pages.
        </p>
      </div>

      {toggleError && (
        <div className="mb-4 px-4 py-3 rounded-md bg-red-50 text-red-600 text-sm border border-red-200">
          {toggleError}
        </div>
      )}

      {/* Built-in pages */}
      <div className="bg-white rounded-lg border border-background-200 overflow-hidden mb-8">
        <div className="px-5 py-4 border-b border-background-100">
          <h3 className="font-heading font-semibold text-base text-foreground-900">Built-in Pages</h3>
          <p className="text-xs text-foreground-500 mt-0.5">
            Toggle a page off to hide it from the website navigation menu.
          </p>
        </div>
        {hiddenLoading ? (
          <p className="px-5 py-6 text-sm text-foreground-500">
            <i className="ri-loader-4-line animate-spin inline-block" aria-hidden="true" /> Loading...
          </p>
        ) : (
          <ul className="divide-y divide-background-100">
            {BUILTIN_PAGES.map((p) => {
              const isHidden = hidden.includes(p.path);
              return (
                <li key={p.path} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <span className="text-sm text-foreground-800 font-medium">{p.label}</span>
                    <span className="block text-xs text-foreground-400">{p.path}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleBuiltin(p.path)}
                    disabled={savingHidden === p.path}
                    role="switch"
                    aria-checked={!isHidden}
                    aria-label={`${isHidden ? 'Show' : 'Hide'} ${p.label}`}
                    className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${
                      isHidden ? 'bg-background-300' : 'bg-primary-500'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${
                        isHidden ? 'left-0.5' : 'left-[22px]'
                      }`}
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Custom pages */}
      <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-background-100 flex items-center justify-between">
          <div>
            <h3 className="font-heading font-semibold text-base text-foreground-900">Custom Pages</h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              Create your own pages (e.g. announcements, campaigns) that appear in the menu.
            </p>
          </div>
          <button type="button" onClick={openAdd} className="btn-primary whitespace-nowrap">
            <i className="ri-add-line" aria-hidden="true" />
            New Page
          </button>
        </div>

        {loading ? (
          <p className="px-5 py-6 text-sm text-foreground-500">
            <i className="ri-loader-4-line animate-spin inline-block" aria-hidden="true" /> Loading...
          </p>
        ) : error ? (
          <div className="px-5 py-6 text-center">
            <p className="text-red-600 text-sm">{error}</p>
            <button type="button" onClick={reload} className="btn-outline mt-3">Retry</button>
          </div>
        ) : pages.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <i className="ri-file-add-line text-4xl text-foreground-300" aria-hidden="true" />
            <p className="mt-3 text-sm text-foreground-500">No custom pages yet. Create your first one.</p>
          </div>
        ) : (
          <ul className="divide-y divide-background-100">
            {pages.map((p) => (
              <li key={p.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-foreground-800 font-medium">{p.title}</span>
                    <Link
                      to={`/page/${p.slug}`}
                      className="text-xs text-primary-600 hover:text-primary-800 underline"
                    >
                      /page/{p.slug}
                    </Link>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-foreground-500">
                    <span className={p.is_published ? 'text-accent-600' : 'text-foreground-400'}>
                      {p.is_published ? 'Published' : 'Draft'}
                    </span>
                    <span>{p.show_in_nav ? 'In menu' : 'Hidden from menu'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => toggleCustom(p, 'is_published')}
                    disabled={busy}
                    title={p.is_published ? 'Unpublish' : 'Publish'}
                    className={`w-8 h-8 flex items-center justify-center rounded-md cursor-pointer transition-colors ${
                      p.is_published ? 'text-accent-600 hover:bg-accent-100' : 'text-foreground-400 hover:bg-background-100'
                    }`}
                  >
                    <i className={p.is_published ? 'ri-eye-line' : 'ri-eye-off-line'} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleCustom(p, 'show_in_nav')}
                    disabled={busy}
                    title={p.show_in_nav ? 'Hide from menu' : 'Show in menu'}
                    className={`w-8 h-8 flex items-center justify-center rounded-md cursor-pointer transition-colors ${
                      p.show_in_nav ? 'text-primary-600 hover:bg-primary-100' : 'text-foreground-400 hover:bg-background-100'
                    }`}
                  >
                    <i className={p.show_in_nav ? 'ri-menu-line' : 'ri-menu-unfold-line'} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => openEdit(p)}
                    title="Edit"
                    className="w-8 h-8 flex items-center justify-center rounded-md text-primary-600 hover:bg-primary-100 cursor-pointer"
                  >
                    <i className="ri-edit-line" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(p.id)}
                    title="Delete"
                    className="w-8 h-8 flex items-center justify-center rounded-md text-red-500 hover:bg-red-50 cursor-pointer"
                  >
                    <i className="ri-delete-bin-line" aria-hidden="true" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Form modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalOpen(false)} aria-hidden="true" />
          <div className="relative bg-white rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
              <h3 className="font-heading font-semibold text-lg text-foreground-900">
                {editingId ? 'Edit Page' : 'New Page'}
              </h3>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="p-2 rounded-md hover:bg-background-100 cursor-pointer"
                aria-label="Close"
              >
                <i className="ri-close-line text-xl" aria-hidden="true" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              {formError && (
                <div className="px-4 py-3 rounded-md bg-red-50 text-red-600 text-sm border border-red-200">
                  {formError}
                </div>
              )}
              <div>
                <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">
                  Page Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="e.g. Youth Conference 2026"
                  className="w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 text-foreground-900"
                />
              </div>
              <div>
                <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">
                  URL Slug <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
                  placeholder="youth-conference-2026"
                  className="w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 text-foreground-900"
                />
                <p className="mt-1 text-xs text-foreground-400">
                  This becomes part of the page link: /page/{slugify(form.slug) || '...'}
                </p>
              </div>
              <div>
                <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">
                  Content
                </label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
                  placeholder="Write the page content here. Use blank lines to separate paragraphs."
                  rows={8}
                  className="w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 text-foreground-900 resize-y"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.show_in_nav}
                  onChange={(e) => setForm((p) => ({ ...p, show_in_nav: e.target.checked }))}
                  className="w-4 h-4 accent-primary-500"
                />
                <span className="text-sm text-foreground-600">Show in website menu</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_published}
                  onChange={(e) => setForm((p) => ({ ...p, is_published: e.target.checked }))}
                  className="w-4 h-4 accent-primary-500"
                />
                <span className="text-sm text-foreground-600">Published (live on the website)</span>
              </label>
            </div>

            <div className="px-6 py-4 border-t border-background-200 flex justify-end gap-3">
              <button type="button" onClick={() => setModalOpen(false)} className="btn-outline whitespace-nowrap">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="btn-primary whitespace-nowrap"
              >
                {saving ? 'Saving...' : 'Save Page'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmDelete(null)} aria-hidden="true" />
          <div className="relative bg-white rounded-xl w-full max-w-sm p-6 shadow-2xl">
            <h3 className="font-heading font-semibold text-lg text-foreground-900">Delete this page?</h3>
            <p className="mt-2 text-sm text-foreground-500">This action cannot be undone.</p>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setConfirmDelete(null)} className="btn-outline whitespace-nowrap">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy}
                className="px-4 py-2.5 rounded-md bg-red-500 text-white text-xs font-label font-medium uppercase tracking-wide hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}